/**
 * The selected report: what it is, its latest file, its schedule, its runs.
 *
 * Preview shows the real latest file from the archive, not a placeholder: a
 * PDF is read back from storage with its checksum, and an Excel workbook says
 * it has to be opened in Excel. When nothing has ever been generated it says
 * that, and offers the one button that fixes it.
 */
import { useEffect, useRef, useState } from 'react'
import { Copy, Download, ExternalLink, FileText, Play, Share2, SlidersHorizontal } from 'lucide-react'
import { reportingCenterService } from '@/services/reportingCenter'
import type { ReportSchedule } from '@/pages/pipeline-intelligence/types'
import { reportTypeSpec } from '../reportTypes'
import { formatBytes, formatWhen } from './format'
import { latestFile, type LibraryReport, type LibraryRun } from './library'
import { DeleteReport } from './DeleteReport'
import { RunsList } from './RunsList'
import { ScheduleCard } from './ScheduleCard'
import { Btn, Eyebrow, KeyValue, Panel, TabList, type TabDef } from './ui'
import type { ReportActions, RunFormat } from './useReportActions'
import type { ReportPermissions } from './useReportingData'

export type ReportHandlers = {
  /** Open the report where it can be changed (the field editor, or Report Studio). */
  open: (report: LibraryReport) => void
  schedule: (report: LibraryReport) => void
  editSchedule: (schedule: ReportSchedule) => void
  share: (report: LibraryReport) => void
  /** The full report page. */
  detail: (report: LibraryReport) => void
}

type PanelTab = 'preview' | 'schedule' | 'runs'

const NO_RUN = 'Your role cannot run reports.'
const NO_EDIT = 'Your role can view and run reports but not change them.'

export function StoredPdfPreview({ run, name }: { run: LibraryRun; name: string }) {
  const [url, setUrl] = useState<string | null>(null)
  const [state, setState] = useState<'idle' | 'loading' | 'error'>('idle')
  // Which request may still answer. A download that finishes after another file was
  // selected (the panel is reused across reports) must not put the old file on screen.
  const request = useRef(0)

  // A different file means a different preview; never leave the last one on screen.
  useEffect(() => {
    request.current += 1
    setUrl(null)
    setState('idle')
    return () => {
      request.current += 1
    }
  }, [run.id])
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url)
    },
    [url],
  )

  async function load() {
    const mine = ++request.current
    setState('loading')
    try {
      const { blob } = await reportingCenterService.downloadHistory(run.id, `${name}.pdf`)
      if (mine !== request.current) return
      setUrl(URL.createObjectURL(blob))
      setState('idle')
    } catch {
      if (mine === request.current) setState('error')
    }
  }

  if (url) {
    return (
      <iframe
        title={`Preview of the latest ${name} file`}
        src={url}
        className="h-[420px] w-full rounded-lg border border-[#242424] bg-white"
      />
    )
  }
  return (
    <div className="flex flex-col items-start gap-2 rounded-lg border border-[#242424] bg-[#0F0F0F] p-4">
      <FileText className="h-5 w-5 text-[#FFD86B]" aria-hidden />
      <p className="text-[12.5px] text-[#B9B9B9]">
        The latest PDF is in the archive. Opening it reads the stored file, not a fresh run.
      </p>
      <Btn size="sm" busy={state === 'loading'} onClick={() => void load()}>
        Show the latest file
      </Btn>
      {state === 'error' ? (
        <p role="alert" className="text-[12px] text-[#F0B08A]">
          The stored file could not be read. It may have been removed from the archive.
        </p>
      ) : null}
    </div>
  )
}

export function ReportPanel({
  report,
  actions,
  perms,
  handlers,
  me,
  now,
  idPrefix = 'panel',
  showOpenPage = true,
}: {
  report: LibraryReport
  actions: ReportActions
  perms: ReportPermissions
  handlers: ReportHandlers
  me: string | null
  now: Date
  idPrefix?: string
  showOpenPage?: boolean
}) {
  const [tab, setTab] = useState<PanelTab>('preview')
  // A new selection starts on Preview. Set while rendering, not in an effect: an effect ran
  // after the panel first painted, so a tab chosen in that moment was reset to Preview.
  const [tabFor, setTabFor] = useState(report.key)
  if (tabFor !== report.key) {
    setTabFor(report.key)
    setTab('preview')
  }

  const definition = report.definition
  const spec = definition ? reportTypeSpec(definition.reportType) : null
  const running = actions.run.isPending && actions.run.variables?.report.key === report.key
  const newest = report.runs[0] ?? null
  const pdf = latestFile(report, 'pdf')
  const xlsx = latestFile(report, 'xlsx')
  const shown = latestFile(report)
  const canRunExcel = spec ? spec.formats.includes('xlsx') && spec.formats[0] !== 'xlsx' : false
  const tabs: Array<TabDef<PanelTab>> = [
    { id: 'preview', label: 'Preview' },
    { id: 'schedule', label: 'Schedule' },
    { id: 'runs', label: 'Runs', badge: report.runs.length ? <span className="text-[11px] tabular-nums text-[#8F8F8F]">{report.runs.length}</span> : undefined },
  ]

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4" data-testid="report-panel">
      <div>
        <Eyebrow>{report.bucket.name}</Eyebrow>
        <h2 className="mt-1 font-display text-[26px] font-semibold leading-[1.1] text-[#F5F3EE]">{report.name}</h2>
        <p className="mt-1 text-[12.5px] text-[#9A9A9A]">
          {[report.scope, report.version ? `v${report.version}` : null, report.formats].filter(Boolean).join(' · ')}
        </p>
      </div>

      <TabList label="Selected report" tabs={tabs} active={tab} onChange={setTab} idPrefix={idPrefix} variant="pill" />

      <div role="tabpanel" id={`${idPrefix}-panel-${tab}`} aria-labelledby={`${idPrefix}-tab-${tab}`} className="flex flex-col gap-4">
        {tab === 'preview' ? (
          <>
            {shown ? (
              <>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-[13px] font-semibold text-[#ECEAE4]">Latest file</span>
                  <span className="text-[11.5px] text-[#8C8C8C]">
                    {formatWhen(shown.at, { now })} · {formatBytes(shown.sizeBytes)} · {shown.trigger}
                  </span>
                </div>
                {shown.format === 'pdf' ? (
                  <StoredPdfPreview run={shown} name={report.name} />
                ) : (
                  <p className="rounded-lg border border-[#242424] bg-[#0F0F0F] p-4 text-[12.5px] text-[#B9B9B9]">
                    The latest file is an Excel workbook. Download it to open it in Excel.
                  </p>
                )}
              </>
            ) : newest && !newest.ok ? (
              <p role="status" className="rounded-lg border border-[#4A3220] bg-[#140E0A] p-4 text-[12.5px] text-[#F0B08A]">
                The latest run did not produce a file. {newest.note}
              </p>
            ) : (
              <p role="status" className="rounded-lg border border-dashed border-[#2A2A2A] p-4 text-[12.5px] text-[#8F8F8F]">
                {report.runs.length
                  ? 'The files this report made are no longer in the archive. Run it again to make a new one.'
                  : 'Nothing has been generated for this report yet. Run it to make the first file.'}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Btn
                variant="primary"
                icon={<Play className="h-3.5 w-3.5" aria-hidden />}
                busy={running}
                disabled={!perms.canRun}
                title={perms.canRun ? undefined : NO_RUN}
                onClick={() => actions.run.mutate({ report })}
                aria-label={`Run ${report.name} now`}
              >
                Run now
              </Btn>
              {canRunExcel ? (
                <Btn
                  disabled={!perms.canRun || running}
                  title={perms.canRun ? undefined : NO_RUN}
                  onClick={() => actions.run.mutate({ report, format: 'xlsx' as RunFormat })}
                  aria-label={`Run ${report.name} now as Excel`}
                >
                  Run as Excel
                </Btn>
              ) : null}
              {pdf ? (
                <Btn
                  icon={<Download className="h-3.5 w-3.5" aria-hidden />}
                  disabled={!perms.canRun || actions.downloadStored.isPending}
                  onClick={() => actions.downloadStored.mutate({ run: pdf, name: report.name })}
                >
                  Download PDF
                </Btn>
              ) : null}
              {xlsx ? (
                <Btn
                  icon={<Download className="h-3.5 w-3.5" aria-hidden />}
                  disabled={!perms.canRun || actions.downloadStored.isPending}
                  onClick={() => actions.downloadStored.mutate({ run: xlsx, name: report.name })}
                >
                  Download Excel
                </Btn>
              ) : null}
            </div>
            <p className="text-[11.5px] text-[#8C8C8C]">
              Run now makes a fresh file. Nothing is emailed and no schedule is affected.
            </p>
          </>
        ) : null}

        {tab === 'schedule' ? (
          report.health.length ? (
            <>
              {report.health.map(({ schedule, health }) => (
                <ScheduleCard
                  key={schedule.id}
                  schedule={schedule}
                  health={health}
                  actions={actions}
                  perms={perms}
                  me={me}
                  now={now}
                  onEdit={handlers.editSchedule}
                />
              ))}
              <Btn size="sm" variant="quiet" disabled={!perms.canEdit} title={perms.canEdit ? undefined : NO_EDIT} onClick={() => handlers.schedule(report)}>
                Add another schedule
              </Btn>
            </>
          ) : (
            <Panel className="flex flex-col gap-3 p-4">
              <dl>
                <KeyValue label="Runs">Not scheduled</KeyValue>
              </dl>
              <p className="text-[12.5px] text-[#B9B9B9]">
                No schedule. Run it by hand, or schedule it. A new schedule is saved paused and starts only when
                someone with approval activates it.
              </p>
              <Btn
                size="sm"
                icon={<SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />}
                disabled={!perms.canEdit}
                title={perms.canEdit ? undefined : NO_EDIT}
                onClick={() => handlers.schedule(report)}
                className="self-start"
              >
                Schedule this report
              </Btn>
            </Panel>
          )
        ) : null}

        {tab === 'runs' ? <RunsList report={report} actions={actions} perms={perms} now={now} compact /> : null}
      </div>

      <div className="mt-auto flex flex-wrap items-center gap-2 border-t border-[#1B1B1B] pt-4">
        <Btn
          size="sm"
          icon={<SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />}
          onClick={() => handlers.open(report)}
          aria-label={`${definition ? 'Edit' : 'Configure'} ${report.name}`}
        >
          {definition ? 'Edit definition' : 'Configure'}
        </Btn>
        {definition ? (
          <>
            <Btn
              size="sm"
              icon={<Copy className="h-3.5 w-3.5" aria-hidden />}
              disabled={!perms.canEdit || actions.duplicate.isPending}
              title={perms.canEdit ? undefined : NO_EDIT}
              onClick={() => actions.duplicate.mutate(definition.id)}
              aria-label={`Duplicate ${report.name}`}
            >
              Duplicate
            </Btn>
            <Btn
              size="sm"
              icon={<Share2 className="h-3.5 w-3.5" aria-hidden />}
              disabled={!perms.canEdit}
              title={perms.canEdit ? undefined : NO_EDIT}
              onClick={() => handlers.share(report)}
              aria-label={`Share ${report.name}`}
            >
              Share
            </Btn>
            <DeleteReport report={report} actions={actions} perms={perms} />
          </>
        ) : null}
        {showOpenPage ? (
          <Btn
            size="sm"
            variant="quiet"
            icon={<ExternalLink className="h-3.5 w-3.5" aria-hidden />}
            onClick={() => handlers.detail(report)}
            className="ml-auto"
            aria-label={`Open the full page for ${report.name}`}
          >
            Open full page
          </Btn>
        ) : null}
      </div>

    </div>
  )
}
